/**
 * Public footer used by the landing, /planes and the legal pages.
 *
 * Centralises link lists + contact info so a new legal page is a one-line
 * change here instead of editing every page. Also surfaces the accepted
 * payment methods so visitors (and Culqi onboarding reviewers) see at a
 * glance that we accept Visa/MC/Amex/Yape and that pagos están procesados
 * por una pasarela regulada.
 */

import { Link } from 'react-router-dom';
import { Mail, MessageCircle, Phone } from 'lucide-react';
import { Brand } from '@/components/Brand';
import { contact } from '@/lib/contact';

const LEGAL_LINKS: Array<{ to: string; label: string }> = [
  { to: '/terminos', label: 'Términos y condiciones' },
  { to: '/privacidad', label: 'Privacidad' },
  { to: '/reembolsos', label: 'Reembolsos' },
  { to: '/cookies', label: 'Cookies' },
  { to: '/reclamaciones', label: 'Libro de Reclamaciones' },
];

const NAV_LINKS: Array<{ to: string; label: string }> = [
  { to: '/planes', label: 'Planes y precios' },
  { to: '/faq', label: 'Preguntas frecuentes' },
  { to: '/contacto', label: 'Contacto' },
];

/** Medios de pago aceptados — render como badges para que Culqi (y los usuarios) los vean. */
const PAYMENT_METHODS: string[] = ['Visa', 'Mastercard', 'Amex', 'Diners', 'Yape'];

export function SiteFooter() {
  return (
    <footer
      className="border-t"
      style={{ borderColor: 'var(--border-subtle)', background: 'var(--surface-1)' }}
    >
      <div className="mx-auto max-w-7xl px-6 py-10">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
          {/* Brand + tagline */}
          <div className="md:col-span-1">
            <div className="mb-3">
              <Brand size="md" />
            </div>
            <p className="text-[12px]" style={{ color: 'var(--text-muted)', lineHeight: 1.55 }}>
              Cálculo estructural de concreto armado según Norma Peruana E.060 y ACI 318-19.
              Parte del ecosistema Alpha+.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <div
              className="mb-2 text-[10px] uppercase tracking-[0.18em]"
              style={{ color: 'var(--text-muted)' }}
            >
              Explora
            </div>
            <ul className="space-y-1.5 text-[13px]">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="no-underline hover:underline"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <div
              className="mb-2 text-[10px] uppercase tracking-[0.18em]"
              style={{ color: 'var(--text-muted)' }}
            >
              Legal
            </div>
            <ul className="space-y-1.5 text-[13px]">
              {LEGAL_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="no-underline hover:underline"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <div
              className="mb-2 text-[10px] uppercase tracking-[0.18em]"
              style={{ color: 'var(--text-muted)' }}
            >
              Soporte
            </div>
            <ul className="space-y-2 text-[13px]">
              <li>
                <a
                  href={`mailto:${contact.supportEmail}`}
                  className="inline-flex items-center gap-1.5 no-underline hover:underline"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <Mail size={13} strokeWidth={1.8} style={{ color: 'var(--accent)' }} />
                  {contact.supportEmail}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${contact.phoneTel}`}
                  className="inline-flex items-center gap-1.5 no-underline hover:underline"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <Phone size={13} strokeWidth={1.8} style={{ color: 'var(--accent)' }} />
                  {contact.phoneDisplay}
                </a>
              </li>
              <li>
                <a
                  href={`https://wa.me/${contact.phoneWa}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 no-underline hover:underline"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <MessageCircle size={13} strokeWidth={1.8} style={{ color: 'var(--accent)' }} />
                  WhatsApp
                </a>
              </li>
              <li className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>
                {contact.supportHours}
              </li>
            </ul>
          </div>
        </div>

        {/* Medios de pago */}
        <div
          className="mt-8 flex flex-col items-start gap-3 border-t pt-6 sm:flex-row sm:items-center sm:justify-between"
          style={{ borderColor: 'var(--border-subtle)' }}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="text-[10.5px] uppercase tracking-[0.18em]"
              style={{ color: 'var(--text-muted)' }}
            >
              Aceptamos
            </span>
            {PAYMENT_METHODS.map((m) => (
              <span
                key={m}
                className="inline-flex h-6 items-center rounded-[var(--radius-sm)] border px-2 text-[11px] tabular"
                style={{
                  background: 'var(--surface-2)',
                  borderColor: 'var(--border-default)',
                  color: 'var(--text-secondary)',
                }}
              >
                {m}
              </span>
            ))}
          </div>
          <div className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>
            Pagos procesados de forma segura por <strong style={{ color: 'var(--text-secondary)' }}>Culqi</strong>{' '}
            (Niubiz / VisaNet). No almacenamos datos de tarjeta.
          </div>
        </div>

        {/* Copyright */}
        <div
          className="mt-6 flex flex-col items-start justify-between gap-2 border-t pt-5 text-[11px] sm:flex-row sm:items-center"
          style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-muted)' }}
        >
          <div>
            © 2026 {contact.legalName}
            {contact.legalRuc && ` · RUC ${contact.legalRuc}`}
          </div>
          <div>Hecho en {contact.legalAddress} 🇵🇪</div>
        </div>
      </div>
    </footer>
  );
}
